import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Portal } from './Portal';
import { cx } from './cx';
import s from './Dropdown.module.css';

export interface DropdownItem {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
  onSelect?: () => void;
  danger?: boolean;
  disabled?: boolean;
  /** Séparateur affiché avant l'entrée. */
  separatorBefore?: boolean;
}

export interface DropdownProps {
  /** Rend le bouton déclencheur ; reçoit les props ARIA à répandre dessus. */
  trigger: (props: {
    onClick: () => void;
    'aria-haspopup': 'menu';
    'aria-expanded': boolean;
    'aria-controls': string;
    ref: (el: HTMLButtonElement | null) => void;
  }) => ReactNode;
  items: DropdownItem[];
  align?: 'start' | 'end';
  /** En-tête non interactif (ex. identité de l'utilisateur). */
  header?: ReactNode;
}

/** Menu déroulant accessible (rôle menu, navigation aux flèches, Échap). */
export function Dropdown({ trigger, items, align = 'end', header }: DropdownProps) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const triggerEl = useRef<HTMLButtonElement | null>(null);
  const menu = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<CSSProperties>({});

  useEffect(() => {
    if (!open) return;
    // Menu en position fixe (portail) : jamais rogné par un conteneur en overflow
    const rect = root.current!.getBoundingClientRect();
    const rtl = document.documentElement.dir === 'rtl';
    const anchorRight = (align === 'end') !== rtl;
    setPos({
      top: rect.bottom + 6,
      minWidth: Math.max(220, rect.width),
      ...(anchorRight ? { right: Math.max(8, window.innerWidth - rect.right) } : { left: Math.max(8, rect.left) }),
    });
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!root.current?.contains(target) && !menu.current?.contains(target)) setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, align]);

  useEffect(() => {
    if (open && pos.top !== undefined) menu.current?.querySelector<HTMLElement>('[role="menuitem"]:not(:disabled)')?.focus();
  }, [open, pos]);

  const close = (refocus = true) => {
    setOpen(false);
    if (refocus) triggerEl.current?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      close();
      return;
    }
    if (e.key === 'Tab') {
      setOpen(false);
      return;
    }
    const els = Array.from(menu.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') ?? []);
    const i = els.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      els[(i + 1) % els.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      els[(i - 1 + els.length) % els.length]?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      els[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      els[els.length - 1]?.focus();
    }
  };

  return (
    <div className={s.root} ref={root}>
      {trigger({
        onClick: () => setOpen((o) => !o),
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-controls': id,
        ref: (el) => {
          triggerEl.current = el;
        },
      })}
      {open && (
        <Portal>
        <div id={id} ref={menu} role="menu" className={s.menu} style={pos} onKeyDown={onKeyDown}>
          {header && <div className={s.header}>{header}</div>}
          {items.map((it) => (
            <div key={it.id} role="none">
              {it.separatorBefore && <hr className={s.sep} />}
              <button
                type="button"
                role="menuitem"
                disabled={it.disabled}
                className={cx(s.item, it.danger && s.danger)}
                onClick={() => {
                  close(false);
                  it.onSelect?.();
                }}
              >
                {it.icon}
                <span>{it.label}</span>
              </button>
            </div>
          ))}
        </div>
        </Portal>
      )}
    </div>
  );
}
